import '../Table/Table.css';

import { ReactElement } from 'react';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { AcronymNameType, AcronymType } from '../../types/AcronymTypes';
import Table from '../Table/Table';

type IType = 'string' | 'boolean' | 'numeric' | 'date' | 'datetime' | 'time' | 'currency';
const datetime: IType = 'datetime';

type IEditable = 'never' | 'always' | 'onUpdate' | 'onAdd';
const never: IEditable = 'never';

const renderType = (type?: AcronymNameType): string => {
  return !type ? AcronymNameType.Jargon : type === AcronymNameType.MissingWord ? 'Missing word' : type;
};

const renderName = (name?: string): JSX.Element => (
  <div className="ColumnName">
    <span>{name}</span>
    <a href={`https://www.acronymfinder.com/${name}.html`} target="_blank" rel="noreferrer">
      <span>
        <FontAwesomeIcon icon={faExternalLinkAlt} />
      </span>
    </a>
  </div>
);

const dynamicObject = [
  { id: AcronymNameType.MissingWord.toLowerCase(), type: 'Missing word' },
  { id: AcronymNameType.Jargon.toLowerCase(), type: 'Jargon' },
];

interface TableProps {
  title: string | ReactElement;
  data: Array<AcronymType>;
  loading: boolean;
  onRowClick: (row: any) => void;
}

const AcronymTable = ({ title, data, loading, onRowClick }: TableProps): JSX.Element => {
  var obj = dynamicObject.reduce(function (acc, cur, i) {
    acc[cur.id] = cur.type;
    return acc;
  }, {});

  const columns = [
    { title: 'Document ID', field: 'documentId', editable: never, filtering: false },
    {
      title: 'Name',
      field: 'name',
      render: (rowData: AcronymType) => renderName(rowData.name),
      editable: never,
      filtering: false,
    },
    {
      title: 'Type',
      field: 'type',
      editable: never,
      render: (rowData: AcronymType) => renderType(rowData.type),
      customFilterAndSearch: (term: any, rowData: AcronymType): boolean =>
        renderType(rowData.type).toLowerCase().indexOf(term) !== -1,
      lookup: obj,
      defaultFilter:
        localStorage.getItem('acronym-filter') !== null && localStorage.getItem('acronym-filter')
          ? [`${localStorage.getItem('acronym-filter')}`]
          : [],
    },
    { title: 'Client ID', field: 'clientId', editable: never, hidden: true },
    { title: 'State', field: 'state', hidden: true },
    {
      title: 'Pronounce',
      field: 'pronounce',
      hidden: true,
    },
    {
      title: 'Add?',
      field: 'addToDict',
      hidden: true,
      render: (rowData: AcronymType): string => (rowData.addToDict ? 'Yes' : 'No'),
      lookup: { true: 'Yes', false: 'No' },
      customFilterAndSearch: (term: any, rowData: AcronymType): boolean =>
        (rowData.addToDict ? 'Yes' : 'No').indexOf(term) !== -1,
    },
    {
      title: 'Skip?',
      field: 'skip',
      hidden: true,
      render: (rowData: AcronymType): string => (rowData.skip ? 'Yes' : 'No'),
      lookup: { true: 'Yes', false: 'No' },
      customFilterAndSearch: (term: any, rowData: AcronymType): boolean =>
        (rowData.addToDict ? 'Yes' : 'No').indexOf(term) !== -1,
    },
    {
      title: 'Created',
      field: 'created',
      type: datetime,
      editable: never,
      filtering: false,
    },
    {
      title: 'Resolved',
      field: 'resolved',
      hidden: true,
      type: datetime,
      editable: never,
    },
  ];

  return <Table loading={loading} columns={columns} data={data} title={title} onRowClick={onRowClick} hasFiltering={true} />;
};

export default AcronymTable;
